(() => {
  'use strict';
  if (window.KinoraCart) return;
  const routeRoot = window.Shopify?.routes?.root || '/';
  // A checkout reminder, not a stock reservation or a price expiry promise.
  const timerKey = 'kinora-cart-reminder-deadline-v1';
  let timerDeadline = 0;
  try { timerDeadline = Number(sessionStorage.getItem(timerKey)) || 0; } catch { /* memory fallback */ }
  function renderTimer() {
    const seconds = Math.max(0, Math.ceil((timerDeadline - Date.now()) / 1000));
    document.querySelectorAll('[data-cart-reminder]').forEach(timer => {
      timer.hidden = !timerDeadline || seconds === 0;
      const clock = timer.querySelector('[data-cart-time]');
      if (clock) clock.textContent = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
      timer.style.setProperty('--kn-reminder-progress', String(Math.min(1, seconds / 600)));
    });
  }
  function startTimer() {
    if (!timerDeadline) {
      timerDeadline = Date.now() + 600000;
      try { sessionStorage.setItem(timerKey, String(timerDeadline)); } catch { /* memory fallback */ }
    }
    renderTimer();
  }
  function resetTimer() {
    timerDeadline = 0;
    try { sessionStorage.removeItem(timerKey); } catch { /* memory fallback */ }
    renderTimer();
  }
  if (document.querySelector('[data-cart-reminder]')) startTimer();
  else if (document.querySelector('[data-cart-drawer-section]')?.dataset.cartItemCount === '0') resetTimer();
  setInterval(() => { if (!document.hidden) renderTimer(); }, 1000);
  document.addEventListener('visibilitychange', renderTimer);

  let busy = false;
  let previousFocus = null;
  let previousOverflow = '';
  let refreshPromise = null;
  const getDialog = () => document.querySelector('[data-cart-drawer]');
  const setStatus = text => { const status = getDialog()?.querySelector('[data-cart-drawer-status]'); if (status) status.textContent = text; };
  const setBusy = state => {
    busy = state;
    const dialog = getDialog();
    dialog?.setAttribute('aria-busy', String(state));
    dialog?.querySelectorAll('[data-cart-change], [data-cart-quantity], [name="checkout"]').forEach(control => { if ('disabled' in control) control.disabled = state || control.hasAttribute('data-cart-at-limit'); });
    dialog?.querySelectorAll('[data-cart-quick-checkout]').forEach(control => { control.inert = state; });
  };
  function unlock() {
    document.documentElement.style.overflow = previousOverflow;
    if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    previousFocus = null;
  }
  function install(html) {
    const incoming = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-drawer-section]');
    const current = document.querySelector('[data-cart-drawer-section]');
    if (!incoming || !current) throw new Error('Your cart could not be displayed. Please open the cart page.');
    const incomingDialog = incoming.querySelector('[data-cart-drawer]');
    const currentDialog = current.querySelector('[data-cart-drawer]');
    // Keep the open native dialog shell so quantity updates do not replay the entrance.
    currentDialog.innerHTML = incomingDialog.innerHTML;
    current.dataset.cartItemCount = incoming.dataset.cartItemCount;
    document.querySelectorAll('[data-cart-count]').forEach(count => { count.textContent = incoming.dataset.cartItemCount || '0'; count.hidden = Number(incoming.dataset.cartItemCount || 0) === 0; });
    if (currentDialog.querySelector('[data-cart-reminder]')) startTimer(); else resetTimer();
    currentDialog.removeAttribute('aria-busy');
    window.Shopify?.PaymentButton?.init?.();
    return currentDialog;
  }
  async function refresh() {
    if (refreshPromise) return refreshPromise;
    refreshPromise = (async () => {
      const response = await fetch(`${routeRoot}cart?section_id=kinora-cart-drawer`, { headers: { Accept: 'text/html' }, cache: 'no-store' });
      if (!response.ok) throw new Error('Your cart could not be refreshed. Please try again.');
      return install(await response.text());
    })();
    try { return await refreshPromise; } finally { refreshPromise = null; }
  }
  async function open({ refresh: shouldRefresh = true, returnFocus = null } = {}) {
    const trigger = returnFocus || document.activeElement;
    try {
      const dialog = shouldRefresh ? await refresh() : getDialog();
      if (!dialog) throw new Error('Cart unavailable');
      if (!dialog.open) {
        previousFocus = trigger;
        previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        dialog.showModal();
      }
      return true;
    } catch {
      // The item may already be added. Navigate to the real cart without adding it again.
      window.location.assign(`${routeRoot}cart`);
      return false;
    }
  }
  async function change(lineKey, quantity, focusHint = null) {
    if (busy) return;
    setBusy(true);setStatus('Updating your cart…');
    try {
      const response = await fetch(`${routeRoot}cart/change.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'This quantity is not available.');
      await refresh();
      if (focusHint) {
        const controls = [...getDialog().querySelectorAll('[data-line-key]')];
        const variantPrefix = lineKey.split(':')[0] + ':';
        const sameLine = controls.filter(control => control.dataset.lineKey === lineKey || control.dataset.lineKey?.startsWith(variantPrefix));
        const focus = sameLine.find(control => focusHint === 'input' ? control.matches('[data-cart-quantity]') : control.getAttribute('aria-label') === focusHint);
        (focus || getDialog().querySelector('[data-cart-drawer-close]'))?.focus({ preventScroll: true });
      }
      setStatus('Cart updated.');
    } catch (error) { setStatus(error.message); }
    finally { setBusy(false); }
  }
  document.addEventListener('click', event => {
    const closeButton = event.target.closest('[data-cart-drawer-close]');
    if (closeButton) { getDialog()?.close();return; }
    const opener = event.target.closest('[data-open-cart]');
    if (opener && !event.ctrlKey && !event.metaKey && !event.shiftKey && event.button === 0) {
      event.preventDefault();open();return;
    }
    const control = event.target.closest('[data-cart-change]');
    if (control && getDialog()?.contains(control)) {
      event.preventDefault();change(control.dataset.lineKey, Number(control.dataset.quantity), control.getAttribute('aria-label'));return;
    }
    const dialog = getDialog();
    if (event.target === dialog) {
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
    }
  });
  document.addEventListener('change', event => {
    const input = event.target.closest('[data-cart-quantity]');
    if (!input || !getDialog()?.contains(input)) return;
    const quantity = Number(input.value);const maximum = input.max ? Number(input.max) : Infinity;
    if (!Number.isInteger(quantity) || quantity < 0 || quantity > maximum) {
      input.value = input.defaultValue;setStatus(Number.isFinite(maximum) ? 'Choose 1, 2 or 3 Kinora straps; each includes a free strap.' : 'Choose a whole quantity.');return;
    }
    change(input.dataset.lineKey, quantity * (Number(input.dataset.cartMultiplier) || 1), 'input');
  });
  document.addEventListener('close', event => { if (event.target.matches?.('[data-cart-drawer]')) unlock(); }, true);
  window.KinoraCart = { open, refresh, startTimer };
})();
